import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9pe006b4qjhggaeps90', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        if (serverMsg && serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试。');
    } finally {
      setSubmitting(false);
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫网',
    url: 'https://www.hanmanwang.com/',
    description: '韩漫网提供韩漫漫画免费阅读服务，无删减韩漫下拉式免费在线阅读。环境纯净无弹窗广告。',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@hanmanwang.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫网上的韩漫漫画是否免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，韩漫网上的所有韩漫漫画均完全免费阅读。我们提供无删减韩漫下拉式免费在线阅读服务，无需付费即可畅享全站内容。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫网上的漫画有弹窗广告吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫网坚持提供纯净的阅读环境，无任何弹窗广告干扰。您可以不受打扰地沉浸在韩漫漫画的阅读乐趣中。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫网上的韩漫漫画是否有删减？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫网上的所有韩漫漫画均为无删减版本，完整呈现原作内容，确保读者能够享受原汁原味的阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫网多久更新一次漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫网每日更新海量韩漫漫画资源，各热门作品保持稳定更新频率，确保读者能够及时追更喜爱的漫画。',
        },
      },
      {
        '@type': 'Question',
        name: '如何使用韩漫网的下拉式阅读功能？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫网采用下拉式阅读设计，您只需进入任意漫画的章节页面，向下滚动即可流畅阅读。无需翻页或点击，提供最舒适的韩漫阅读体验。',
        },
      },
    ],
  };

  const stats = [
    { value: '120+', label: '漫画资源' },
    { value: '50万+', label: '月活读者' },
    { value: '98%', label: '用户好评率' },
    { value: '3年+', label: '运营时间' },
  ];

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: '关于我们' },
                ]}
              />

              <section className="mt-6 mb-14">
                <div className="text-center max-w-2xl mx-auto">
                  <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                    关于韩漫网
                  </h1>
                  <p className="text-sm text-foreground-500 mt-4 leading-relaxed">
                    韩漫网是一个专注于韩漫漫画免费阅读的中文平台，提供无删减韩漫下拉式免费在线阅读服务。
                    我们致力于为韩漫爱好者打造一个纯净、便捷、舒适的阅读空间。
                  </p>
                </div>
              </section>

              <section className="mb-14">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
                  {stats.map((stat) => (
                    <div key={stat.label} className="text-center p-6 rounded-lg bg-background-50 border border-background-200/60">
                      <div className="text-2xl md:text-3xl font-bold text-primary-500">{stat.value}</div>
                      <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-14">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">
                      品牌故事
                    </h2>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      韩漫网成立于三年前，由一群热爱韩国漫画的年轻人共同创建。我们深知韩漫爱好者在寻找高质量、
                      无删减漫画资源时遇到的困难——弹窗广告、付费墙、内容删减等问题严重影响了阅读体验。
                    </p>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      因此，我们决定打造一个真正为读者服务的韩漫漫画免费阅读平台。在这里，您可以找到恋爱、
                      奇幻、动作、悬疑、搞笑等各种类型的韩漫资源，全部无删减、无弹窗广告，支持下拉式流畅阅读。
                    </p>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      三年来，韩漫网已累计服务超过五十万月活读者，获得了98%的用户好评率。我们将继续努力，
                      为韩漫爱好者提供更优质的韩漫在线阅读体验。
                    </p>
                  </div>
                  <div className="rounded-lg overflow-hidden aspect-video">
                    <img
                      src="https://readdy.ai/api/search-image?query=Warm%20cozy%20office%20workspace%20with%20bookshelves%20filled%20with%20comics%20and%20manga%2C%20young%20creative%20team%20discussing%20around%20wooden%20table%2C%20soft%20natural%20light%20from%20large%20windows%2C%20plants%20decorating%20the%20space%2C%20relaxed%20atmosphere%2C%20warm%20peach%20and%20cream%20tones%2C%20editorial%20photography%20style%2C%20clean%20minimal%20aesthetic&width=800&height=450&seq=about-team-01&orientation=landscape"
                      alt="韩漫网团队 - 韩漫漫画免费阅读平台"
                      title="韩漫网团队"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </section>

              <section className="mb-14">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6 text-center">
                  我们的承诺
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                    {
                      icon: 'ri-shield-check-line',
                      title: '内容完整',
                      desc: '所有韩漫漫画均为无删减版本，保留原作全部内容，拒绝任何形式的删减或修改。',
                    },
                    {
                      icon: 'ri-advertisement-line',
                      title: '无广告干扰',
                      desc: '全站无弹窗广告，为您提供纯净的韩漫下拉式免费在线阅读环境。',
                    },
                    {
                      icon: 'ri-smartphone-line',
                      title: '下拉式阅读',
                      desc: '采用流畅的下拉式阅读设计，无需翻页操作，让韩漫阅读更加舒适自然。',
                    },
                    {
                      icon: 'ri-refresh-line',
                      title: '每日更新',
                      desc: '海量韩漫资源每日更新，热门作品保持稳定更新频率，不错过任何精彩内容。',
                    },
                    {
                      icon: 'ri-group-line',
                      title: '用户至上',
                      desc: '以读者需求为核心，持续优化韩漫在线阅读体验，打造用户喜爱的漫画平台。',
                    },
                    {
                      icon: 'ri-lock-line',
                      title: '安全可靠',
                      desc: '网站采用先进的安全技术，保护用户隐私，让您安心享受韩漫漫画阅读。',
                    },
                  ].map((item) => (
                    <div key={item.title} className="p-6 rounded-lg bg-background-50 border border-background-200/60">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                        <i className={`${item.icon} text-lg text-primary-500`}></i>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2">{item.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-14">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6 text-center">
                  常见问题
                </h2>
                <div className="max-w-3xl mx-auto flex flex-col gap-4">
                  {[
                    {
                      q: '韩漫网上的韩漫漫画是否免费？',
                      a: '是的，韩漫网上的所有韩漫漫画均完全免费阅读。我们提供无删减韩漫下拉式免费在线阅读服务，无需付费即可畅享全站内容。',
                    },
                    {
                      q: '韩漫网上的漫画有弹窗广告吗？',
                      a: '韩漫网坚持提供纯净的阅读环境，无任何弹窗广告干扰。您可以不受打扰地沉浸在韩漫漫画的阅读乐趣中。',
                    },
                    {
                      q: '韩漫网上的韩漫漫画是否有删减？',
                      a: '韩漫网上的所有韩漫漫画均为无删减版本，完整呈现原作内容，确保读者能够享受原汁原味的韩漫阅读体验。',
                    },
                    {
                      q: '韩漫网多久更新一次漫画？',
                      a: '韩漫网每日更新海量韩漫漫画资源，各热门作品保持稳定更新频率，确保读者能够及时追更喜爱的漫画。',
                    },
                    {
                      q: '如何使用韩漫网的下拉式阅读功能？',
                      a: '韩漫网采用下拉式阅读设计，您只需进入任意漫画的章节页面，向下滚动即可流畅阅读。无需翻页或点击，提供最舒适的韩漫阅读体验。',
                    },
                  ].map((faq, index) => (
                    <details key={index} className="group rounded-lg bg-background-50 border border-background-200/60 overflow-hidden">
                      <summary className="flex items-center justify-between p-5 cursor-pointer">
                        <span className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</span>
                        <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center transition-transform duration-200 group-open:rotate-45">
                          <i className="ri-add-line text-foreground-500"></i>
                        </div>
                      </summary>
                      <div className="px-5 pb-5">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              <section className="mb-14" id="contact">
                <div className="max-w-2xl mx-auto">
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6 text-center">
                    联系我们
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                    {[
                      { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@hanmanwang.com' },
                      { icon: 'ri-customer-service-line', label: '在线客服', value: '工作日 9:00 - 18:00' },
                      { icon: 'ri-question-answer-line', label: '反馈建议', value: '通过下方表单提交' },
                    ].map((item) => (
                      <div key={item.label} className="text-center p-5 rounded-lg bg-background-50 border border-background-200/60">
                        <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                          <i className={`${item.icon} text-lg text-secondary-600`}></i>
                        </div>
                        <h4 className="text-xs font-semibold text-foreground-900 mb-1">{item.label}</h4>
                        <p className="text-xs text-foreground-500">{item.value}</p>
                      </div>
                    ))}
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100/50"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100/50"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100/50"
                        placeholder="请输入主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={5}
                        maxLength={500}
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100/50 resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? '提交中...' : '发送留言'}
                    </button>

                    {formStatus === 'success' && (
                      <div className="p-3 rounded-lg bg-accent-50 text-accent-700 text-sm text-center">
                        感谢您的留言！我们会尽快回复您。
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="p-3 rounded-lg bg-primary-50 text-primary-700 text-sm text-center">
                        {formError}
                      </div>
                    )}
                  </form>
                </div>
              </section>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}